import { useForm } from "react-hook-form";
import "./setter-form.css";
import { FaCircleCheck } from "react-icons/fa6";
import { IoMdAdd } from "react-icons/io";
import { MdDelete, MdModeEdit } from "react-icons/md";
import { FaTimes } from "react-icons/fa";
import Swal from "sweetalert2";
import {
  addData,
  getData,
  formService,
  getBtnEmmit,
  setConfirmationModal,
  formResetService,
  updateDataByIndex,
  removeDataByIndex,
} from "./setter-form-service";
import { useEffect, useRef, useState } from "react";
import axios from "axios";

function SetterForm() {
  const [destinationArr, setDestinationArr] = useState([]);
  const [inputConsignerValue, setInputConsignerValue] = useState("");
  const [debouncedValue, setDebouncedValue] = useState("");
  const [consigneeArr, setConsigneeArr] = useState([]);
  const [numberOfPallets, setNumberOfPallets] = useState(0);
  const [palletWeights, setPalletWeights] = useState([]);
  const [totalPieces, setTotalPieces] = useState(0);
  const [records, setRecords] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [topClientsArr, setTopClientsArr] = useState([]);

  const {
    register,
    handleSubmit,
    reset,
    unregister,
    setValue,
    trigger,
    watch,
    formState: { errors, isSubmitted },
  } = useForm({
    mode: "onChange",
    shouldFocusError: true,
    defaultValues: { number_pallets: "" },
  });

  let formBtnClick = false;
  const inputConsignee = useRef(null);

  const consigneName = watch("consigne_name");

  useEffect(() => {
    const subscription = formService.formSubmitSubject.subscribe(async () => {
      const isValidForm = await trigger();
      if (isValidForm && getBtnEmmit()) {
        handleSubmit(setterSubmit)();
      } else if (!isValidForm && getBtnEmmit()) {
        if (getData().length < 1) {
          handleSubmit(setterSubmit)();
        } else {
          setConfirmationModal(true);
          return;
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [handleSubmit, trigger]);

  useEffect(() => {
    getDestinations();
  }, []);

  // Efecto para manejar el debounce y la búsqueda
  useEffect(() => {
    // Resetear estados cuando el campo consigne_name se vacía (incluyendo después de reset)
    if (!consigneName) {
      setInputConsignerValue("");
      setDebouncedValue("");
      setConsigneeArr([]);
      return;
    }

    const handler = setTimeout(() => {
      setDebouncedValue(consigneName);
    }, 500);

    return () => clearTimeout(handler);
  }, [consigneName]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (inputConsignerValue) {
        setDebouncedValue(inputConsignerValue);
      } else {
        setConsigneeArr([]);
      }
    }, 500);

    return () => clearTimeout(handler);
  }, [inputConsignerValue]);

  // Efecto para realizar la búsqueda
  useEffect(() => {
    if (debouncedValue && debouncedValue.trim() !== "") {
      const fetchData = async () => {
        try {
          const url = `${process.env.REACT_APP_URL_DOMESTIC_BACKEND_HEAVY_LOAD}/setter/search?query=${debouncedValue}`;

          const response = await axios.get(url, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem(
                "heavy-load-token"
              )}`,
            },
          });
          if (response.data.data.results.length === 0) {
            setConsigneeArr([]);
          } else {
            setConsigneeArr(response.data.data.results);
          }
        } catch (error) {
          console.error(error);
          setConsigneeArr([]);
        }
      };
      fetchData();
    } else {
      setConsigneeArr([]);
    }
  }, [debouncedValue]);

  useEffect(() => {
    const subscription = formResetService.onReset().subscribe(() => {
      reset();
      setInputConsignerValue("");
      setDebouncedValue("");
      setConsigneeArr([]);
    });

    return () => subscription.unsubscribe();
  }, [reset]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        inputConsignee.current &&
        !inputConsignee.current.contains(event.target)
      ) {
        setConsigneeArr([]);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Calcular el total de piezas cada vez que cambien los pesos
  useEffect(() => {
    const sum = palletWeights.reduce((acc, curr) => acc + curr, 0);
    setTotalPieces(sum);

    // Actualizar el valor en el formulario (si estás usando react-hook-form)
    if (setValue) {
      setValue("pieces", sum.toString());
    }
  }, [palletWeights]);

  // 🔹 Abre el modal y carga datos si es edición
  const openModal = (index = null) => {
    setEditIndex(index);
    if (index !== null) {
      const record = records[index];
      reset(record);
      setNumberOfPallets(record.pallets?.length || 0);
      setPalletWeights(record.pallets?.map((p) => p.pieces) || []);
    } else {
      clearForm();
      setNumberOfPallets(0);
      setPalletWeights([]);
      setTotalPieces(0);
    }
    setIsModalOpen(true);
  };

  const clearForm = () => {
    const fieldsToClear = [
      "customer_name",
      "consigne_name",
      "mount",
      "material",
      "number_pallets",
      "destination_id",
    ];

    fieldsToClear.forEach((field) => setValue(field, ""));
    setValue("destination_id", "destino");

    // Limpia los pallets dinámicos
    const currentValues = watch();
    Object.keys(currentValues).forEach((key) => {
      if (key.startsWith("pallet")) {
        unregister(key);
        setValue(key, "");
      }
    });

    setNumberOfPallets(0);
    setPalletWeights([]);
    setTotalPieces(0);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    clearForm();
    setEditIndex(null);
  };

  const handleOptionClick = (name) => {
    setValue("consigne_name", name);
    setConsigneeArr([]);
  };

  const setterSubmit = async (data) => {
    const palletKeys = Object.keys(data).filter(
      (key) => key.startsWith("pallet") && parseInt(data[key]) > 0
    );

    const pallets = palletKeys
      .map((key) => ({
        number: parseInt(key.replace("pallet", "")),
        pieces: parseInt(data[key]) || 0,
      }))
      .sort((a, b) => a.number - b.number);

    const shipment = {
      customer_name: data.customer_name,
      consigne_name: data.consigne_name,
      number_pallets: data.number_pallets,
      pieces: data.pieces,
      destination_id: data.destination_id,
      mount: data.mount ? data.mount : 0,
      material: data.material ? data.material : "",
      pallets: pallets,
    };

    if (formBtnClick === false) {
      setConfirmationModal(true);
      return;
    }
    if (editIndex !== null) {
      const updated = [...records];
      updated[editIndex] = shipment;
      setRecords(updated);
      updateDataByIndex(editIndex, shipment);
      Swal.fire("Actualizado", "El embarque fue modificado", "success");
    } else {
      const newList = [...records, shipment];
      setRecords(newList);
      addData(shipment);
      Swal.fire("Agregado", "El embarque fue agregado correctamente", "success");
    }

    closeModal();
  };

  const getDestinations = async () => {
    try {
      const url = `${process.env.REACT_APP_URL_DOMESTIC_BACKEND_HEAVY_LOAD}/setter/destination`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("heavy-load-token")}`,
        },
      });
      const filterDestinations = response.data.data.destination.filter(
        (destination) => !destination.name.startsWith("MEX")
      );
      setDestinationArr(filterDestinations);
    } catch (error) {
      console.error(error);
    }
  };

  // Manejar cambios en el número de tarimas
  const handlePalletNumberChange = (e) => {
    const value = e.target.value;
    if (/^[0-9]*$/.test(value)) {
      const num = value === "" ? 0 : parseInt(value);
      const previousCount = numberOfPallets;
      setNumberOfPallets(num);

      if (num > palletWeights.length) {
        setPalletWeights([
          ...palletWeights,
          ...Array(num - palletWeights.length).fill(0),
        ]);
      } else if (num < palletWeights.length) {
        setPalletWeights(palletWeights.slice(0, num));
        for (let i = num + 1; i <= previousCount; i++) {
          unregister(`pallet${i}`);
        }
      }
    }
  };

  // Manejar cambios en el peso de cada tarima
  const handlePalletWeightChange = (index, value) => {
    if (/^[0-9]*$/.test(value)) {
      const newWeights = [...palletWeights];
      newWeights[index] = value === "" ? 0 : parseInt(value);
      setPalletWeights(newWeights);
    }
  };

  const handleDeleteRow = (index) => {
    Swal.fire({
      title: "¿Deseas borrar el turno?",
      iconHtml: `
      <svg width="150" height="150" viewBox="0 0 150 150" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M75 0C116.423 0 150 33.5775 150 75C150 116.423 116.423 150 75 150C33.5775 150 0 116.423 0 75C0 33.5775 33.5775 0 75 0ZM75 105C73.0109 105 71.1032 105.79 69.6967 107.197C68.2902 108.603 67.5 110.511 67.5 112.5C67.5 114.489 68.2902 116.397 69.6967 117.803C71.1032 119.21 73.0109 120 75 120C76.9891 120 78.8968 119.21 80.3033 117.803C81.7098 116.397 82.5 114.489 82.5 112.5C82.5 110.511 81.7098 108.603 80.3033 107.197C78.8968 105.79 76.9891 105 75 105ZM75 33.75C67.7894 33.75 60.8742 36.6144 55.7755 41.713C50.6769 46.8117 47.8125 53.7269 47.8125 60.9375C47.8125 62.9266 48.6027 64.8343 50.0092 66.2408C51.4157 67.6473 53.3234 68.4375 55.3125 68.4375C57.3016 68.4375 59.2093 67.6473 60.6158 66.2408C62.0223 64.8343 62.8125 62.9266 62.8125 60.9375C62.815 58.7253 63.4195 56.5554 64.5614 54.6607C65.7032 52.7659 67.3392 51.2176 69.294 50.1819C71.2488 49.1462 73.4487 48.662 75.6577 48.7814C77.8667 48.9008 80.0015 49.6192 81.8333 50.8596C83.6651 52.0999 85.1248 53.8155 86.0557 55.8223C86.9867 57.8291 87.3539 60.0514 87.118 62.251C86.882 64.4506 86.0518 66.5445 84.7164 68.3082C83.381 70.0719 81.5907 71.4389 79.5375 72.2625C74.4675 74.2875 67.5 79.4775 67.5 88.125V90C67.5 91.9891 68.2902 93.8968 69.6967 95.3033C71.1032 96.7098 73.0109 97.5 75 97.5C76.9891 97.5 78.8968 96.7098 80.3033 95.3033C81.7098 93.8968 82.5 91.9891 82.5 90C82.5 88.17 82.875 87.255 84.4575 86.475L85.11 86.175C90.9661 83.8192 95.8203 79.4996 98.8405 73.9569C101.861 68.4141 102.859 61.9934 101.663 55.7954C100.468 49.5974 97.1541 44.0082 92.2893 39.986C87.4246 35.9638 81.3122 33.7592 75 33.75Z" fill="#035CF7"/>
    </svg>
    
    `,
    showCancelButton: true,
    confirmButtonText: "Sí, eliminar",
    cancelButtonText: "Cancelar",
    customClass: {
      icon: "no-default-icon", 
      confirmButton: "btn-confirm",
  cancelButton: "btn-cancel",
  icon: "no-default-icon"
    },
  }).then((result) => {
    if (result.isConfirmed) {
      removeDataByIndex(index);
      setRecords([...getData()]);
      Swal.fire("Eliminado", "El embarque ha sido eliminado.", "success");
    }
  });
  }

  return (
    <>

      <div className="records">
        <table className="table-format-shepments">
          <thead>
            <tr className="table-header">
              <th className="table-header-cell">Cliente</th>
              <th className="table-header-cell">Consignatario</th>
              <th className="table-header-cell">Información Contenedor</th>
              <th className="table-header-cell">Tarimas</th>
              <th className="table-header-cell">Piezas</th>
              <th className="table-header-cell">Destino</th>

            </tr>
          </thead>
          <tbody>
            {records.length === 0 ? (
              <tr>
                <td colSpan="10" className="text-center">
                  No hay embarques agregados.
                </td>
              </tr>
            ) : (

              records.map((item, index) => {

                const destination_object = destinationArr.find(dest => dest.id === item.destination_id);

                return (
                  <tr key={index}>
                    <td className="borderStyle">{item.customer_name}</td>
                    <td className="borderStyle">{item.consigne_name}</td>
                    <td className="borderStyle">{item.material}</td>
                    <td className="borderStyle">{item.number_pallets}</td>
                    <td className="borderStyle">{item.pieces}</td>
                    <td className="borderStyle"> {destination_object ? destination_object.name : "Sin destino"}</td>
                    <td className="spacing-cell"></td>
                    <td className="text-center actionTdSeek">
                      <div className="box-container seek-bg-red" onClick={() => handleDeleteRow(index)}>
                        <MdDelete />
                      </div>
                      <div className="box-container seek-bg-edit" onClick={() => openModal(index)}>
                        <MdModeEdit />
                      </div>
                    </td>
                  </tr>
                );


              })
            )}

          </tbody>
        </table>
      </div>

      <button className="btn-add-shipment" onClick={() => openModal()}>
        <IoMdAdd /> Agregar embarque
      </button>

      {/* Modal con formulario */}
      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content-shipment">
            <button
              className="close-modal-btn"
              onClick={() => closeModal()}
              type="button"
            >
              <FaTimes />
            </button>
            <h5>Embarque {editIndex !== null
              ? editIndex + 1
              : getData().length + 1}
            </h5>
            <form
              className="setter-form"
              autoComplete="off"
              onSubmit={handleSubmit(setterSubmit)}
            >
              <p>Ingresa los datos de los embarques</p>

              {/* CLIENT NAME */}
              <div className="heavy-form-group">
                <input
                  id="customer_name"
                  type="text"
                  placeholder=" "
                  className="formInput"
                  {...register("customer_name", {
                    required: true,
                  })}
                />
                <label htmlFor="customer_name" className="formLabel">
                  Nombre cliente <span className="text-alert">*</span>
                </label>
                {errors.customer_name?.type === "required" && isSubmitted && (
                  <p>Este campo es requerido</p>
                )}
              </div>

              {/* CONSIGNEE NAME */}
              <div
                className="heavy-form-group consignee-content"
                ref={inputConsignee}
              >
                <input
                  id="consigne_name"
                  type="text"
                  placeholder=" "
                  className="formInput"
                  {...register("consigne_name", {
                    required: true,
                    onChange: (e) => setInputConsignerValue(e.target.value),
                  })}
                  onKeyDown={(e) => {
                    if (e.key === "Tab") {
                      setConsigneeArr([]);
                    }
                  }}
                />
                <label htmlFor="consigne_name" className="formLabel">
                  Nombre consignatario <span className="text-alert">*</span>
                </label>
                <div
                  className={`answer-consignee ${
                    consigneeArr.length === 0 ? "" : "active"
                  }`}
                >
                  {consigneeArr.map((option, index) => (
                    <p key={index} onClick={() => handleOptionClick(option.name)}>
                      {option.name}
                    </p>
                  ))}
                </div>
                {errors.consigne_name?.type === "required" && isSubmitted && (
                  <p>Este campo es requerido</p>
                )}
              </div>

              {/* PK $ */}
              <div className="heavy-form-group">
                <input
                  id="mount"
                  type="text"
                  placeholder=" "
                  className="formInput"
                  {...register("mount", {
                    pattern: {
                      value: /^[0-9]*$/,
                      message: "Solo se permiten números",
                    },
                  })}
                />
                <label htmlFor="mount" className="formLabel">
                  PK $
                </label>
                {errors.mount?.type === "required" && isSubmitted && (
                  <p>Este campo es requerido</p>
                )}
                {errors.mount?.type === "pattern" && <p>{errors.mount?.message}</p>}
              </div>

              {/* Información del contenedor */}
              <div className="heavy-form-group">
                <input
                  id="material"
                  type="text"
                  placeholder=" "
                  className="formInput"
                  {...register("material", {
                    maxLength: {
                      value: 240,
                      message: "Máximo 240 caracteres",
                    },
                  })}
                />
                <label htmlFor="material" className="formLabel">
                  Información del contenedor <span className="text-alert">*</span>
                </label>
                {errors.material?.type === "required" && isSubmitted && (
                  <p>Este campo es requerido</p>
                )}
                {errors.material?.type === "maxLength" && (
                  <p>{errors.material?.message}</p>
                )}
              </div>

              {/* TARIMAS */}
              <div className="heavy-form-group">
                <input
                  id="number_pallets"
                  type="text"
                  placeholder=" "
                  className="formInput"
                  {...register("number_pallets", {
                    required: true,
                    pattern: {
                      value: /^[0-9]*$/,
                      message: "Solo se permiten números",
                    },
                    onChange: (e) => handlePalletNumberChange(e),
                  })}
                />
                <label htmlFor="number_pallets" className="formLabel">
                  Cantidad de tarimas de este embarque{" "}
                  <span className="text-alert">*</span>
                </label>
                {errors.number_pallets?.type === "required" && isSubmitted && (
                  <p>Este campo es requerido</p>
                )}
                {errors.number_pallets?.type === "pattern" && (
                  <p>{errors.number_pallets?.message}</p>
                )}
              </div>

              {numberOfPallets > 0 && (
                <div className="pallet-container">
                  {Array.from({ length: numberOfPallets }).map((_, index) => (
                    <div key={index} className="two-inputs-contain">
                      <div className="heavy-form-group pallet-label">
                        Tarima {index + 1}
                      </div>
                      <div className="heavy-form-group">
                        <input
                          type="text"
                          placeholder=" "
                          className="formInput"
                          value={palletWeights[index] || 0}
                          {...register(`pallet${index + 1}`, {
                            required: true,
                            onChange: (e) =>
                              handlePalletWeightChange(index, e.target.value),
                          })}
                        />
                        <label className="formLabel">Piezas</label>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="two-inputs-contain">
                <div className="heavy-form-group">
                  <input
                    type="text"
                    value={totalPieces}
                    placeholder=" "
                    className="formInput"
                    disabled
                    style={{ backgroundColor: "#a3a3a3" }}
                    {...register("pieces")}
                  />
                  <label className="formLabel">Piezas totales</label>
                </div>

                <div className="heavy-form-group">
                  <select
                    className="formInput"
                    {...register("destination_id", {
                      validate: (value) => value !== "destino",
                    })}
                  >
                    <option value="destino">Destino</option>
                    {destinationArr.map((dest, i) => (
                      <option key={i} value={dest.id}>
                        {dest.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="btn-containe">
                <button
                  type="submit"
                  onClick={() => {
                    formBtnClick = true;
                  }}
                  className={editIndex !== null ? "button-updated" : "button-add"}
                >
                  {editIndex !== null ? (
                    <>
                      <FaCircleCheck /> Actualizar embarque
                    </>
                  ) : (
                    <>
                      <IoMdAdd /> Agregar embarque
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
    </>
  );
}

export default SetterForm;
